import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { platformApiFetch } from './platformApi';

function buildListParams({
  page,
  pageSize,
  appliedQ,
  appliedAction,
  appliedModuleName,
  appliedEntityType,
  appliedEntityId,
  appliedActorUserId,
  appliedDateFrom,
  appliedDateTo,
  sort,
  order
}) {
  const params = new URLSearchParams({
    page: String(page + 1),
    pageSize: String(pageSize),
    sort,
    order
  });
  if (appliedQ) params.set('q', appliedQ);
  if (appliedAction) params.set('action', appliedAction);
  if (appliedModuleName) params.set('moduleName', appliedModuleName);
  if (appliedEntityType) params.set('entityType', appliedEntityType);
  if (appliedEntityId) params.set('entityId', appliedEntityId);
  if (appliedActorUserId) params.set('actorUserId', appliedActorUserId);
  if (appliedDateFrom) params.set('dateFrom', appliedDateFrom);
  if (appliedDateTo) params.set('dateTo', appliedDateTo);
  return params;
}

export default function PlatformAuditLogsListPage() {
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [actionInput, setActionInput] = useState('');
  const [appliedAction, setAppliedAction] = useState('');
  const [moduleInput, setModuleInput] = useState('');
  const [appliedModuleName, setAppliedModuleName] = useState('');
  const [entityTypeInput, setEntityTypeInput] = useState('');
  const [appliedEntityType, setAppliedEntityType] = useState('');
  const [entityIdInput, setEntityIdInput] = useState('');
  const [appliedEntityId, setAppliedEntityId] = useState('');
  const [actorInput, setActorInput] = useState('');
  const [appliedActorUserId, setAppliedActorUserId] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = buildListParams({
      page,
      pageSize,
      appliedQ,
      appliedAction,
      appliedModuleName,
      appliedEntityType,
      appliedEntityId,
      appliedActorUserId,
      appliedDateFrom,
      appliedDateTo,
      sort,
      order
    });
    try {
      const res = await platformApiFetch(`/api/platform/audit-logs?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load audit logs');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    appliedQ,
    appliedAction,
    appliedModuleName,
    appliedEntityType,
    appliedEntityId,
    appliedActorUserId,
    appliedDateFrom,
    appliedDateTo,
    sort,
    order
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedAction(actionInput.trim());
    setAppliedModuleName(moduleInput.trim());
    setAppliedEntityType(entityTypeInput.trim());
    setAppliedEntityId(entityIdInput.trim());
    setAppliedActorUserId(actorInput.trim());
    setAppliedDateFrom(dateFromInput.trim());
    setAppliedDateTo(dateToInput.trim());
  };

  const exportCsv = async () => {
    setExporting(true);
    const params = buildListParams({
      page: 0,
      pageSize,
      appliedQ,
      appliedAction,
      appliedModuleName,
      appliedEntityType,
      appliedEntityId,
      appliedActorUserId,
      appliedDateFrom,
      appliedDateTo,
      sort,
      order
    });
    params.delete('page');
    params.delete('pageSize');
    try {
      const res = await platformApiFetch(`/api/platform/audit-logs/export?${params}`, {
        headers: { Accept: 'text/csv' }
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const json = ct.includes('json') ? await res.json().catch(() => ({})) : {};
        throw new Error(json.error || 'Export failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'platform-audit-logs.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const entries = data?.entries || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Audit logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Platform-wide events (staff). Full IP and user agent are available for investigations.
          </Typography>
        </Box>
        <Tooltip title="Export up to 5000 matching rows (CSV)">
          <span>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
              onClick={exportCsv}
              disabled={exporting}
            >
              Export CSV
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Action, entity, summary, metadata…"
            sx={{ minWidth: 240 }}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <TextField
            size="small"
            label="Action contains"
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="Module"
            value={moduleInput}
            onChange={(e) => setModuleInput(e.target.value)}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="Entity type"
            value={entityTypeInput}
            onChange={(e) => setEntityTypeInput(e.target.value)}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="Entity id"
            value={entityIdInput}
            onChange={(e) => setEntityIdInput(e.target.value)}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="Actor platform user id"
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="From date"
            type="date"
            value={dateFromInput}
            onChange={(e) => setDateFromInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="To date"
            type="date"
            value={dateToInput}
            onChange={(e) => setDateToInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="created_at">Time</MenuItem>
              <MenuItem value="id">ID</MenuItem>
              <MenuItem value="action">Action</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Newest first</MenuItem>
              <MenuItem value="asc">Oldest first</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={applyFilters}>
            Apply filters
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell align="right">Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.created_at ? String(row.created_at) : '—'}
                    </TableCell>
                    <TableCell>
                      {row.actor_email || (row.actor_user_id != null ? `#${row.actor_user_id}` : '—')}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.action}</TableCell>
                    <TableCell>{row.module_name || '—'}</TableCell>
                    <TableCell>
                      {row.entity_type || '—'}
                      {row.entity_id ? ` #${row.entity_id}` : ''}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }} title={row.summary}>
                      {row.summary && row.summary.length > 80 ? `${row.summary.slice(0, 80)}…` : row.summary || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        component={Link}
                        to={`/platform/audit-logs/${row.id}`}
                        size="small"
                        aria-label="View detail"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      No events match these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
